import React from 'react';
import { Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { ArmoryItem, KratosTokenKey, KratosTokens, LoadoutStatsWithRequirements, PowerDieColor, TriskellionStats } from '../../core/types';
import '../../styles.css';
import { Utils } from '../../core/utilities';
import { Constants } from '../../core/constants';
import NumericField from '../../components/NumericField';
import { getArgonautStatPresentation } from '../../core/utilities/token-presentation';
import Button from '../../components/Button';

interface ArmoryEditSectionProps {
    visibleTags: string[];
    editingItem: ArmoryItem;
    setEditingItem: React.Dispatch<React.SetStateAction<ArmoryItem>>;
}

const filterObject = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj
            .map((item) => {
                if (Array.isArray(item)) {
                    return item.filter((die) => Object.values(PowerDieColor).includes(die as PowerDieColor));
                }
                return filterObject(item);
            })
            .filter((item) => item !== null && item !== undefined && (Array.isArray(item) ? item.length !== 0 : Object.keys(item).length !== 0));
    } else if (typeof obj === 'object' && obj !== null) {
        const filteredObj = Object.entries(obj).reduce((acc, [key, value]) => {
            const filteredValue = filterObject(value);
            if (
                filteredValue !== null &&
                filteredValue !== 0 &&
                filteredValue !== '' &&
                !(Array.isArray(filteredValue) && filteredValue.length === 0) &&
                !(typeof filteredValue === 'object' && Object.keys(filteredValue).length === 0)
            ) {
                acc[key] = filteredValue;
            }
            return acc;
        }, {} as any);

        return Object.keys(filteredObj).length > 0 ? filteredObj : undefined;
    }

    return obj;
};

const formatLoadoutInfo = (loadout: LoadoutStatsWithRequirements): string => {
    const filteredLoadout = filterObject(loadout);
    return 'LOADOUT DETAILS\n' + JSON.stringify(filteredLoadout, null, 2);
};

const ARMORY_TOKEN_FIELDS: Array<{ key: KratosTokenKey; label: string; spoiler?: string }> = [
    { key: 'break', label: 'Auto-Break' },
    { key: 'black', label: 'Auto-Black', spoiler: Constants.SpoilerTag.CYCLE_II },
    { key: 'fire', label: 'Auto-Fire', spoiler: Constants.SpoilerTag.CYCLE_II },
    { key: 'hope', label: 'Auto-Hope', spoiler: Constants.SpoilerTag.CYCLE_III },
];

const REQUIREMENT_FIELDS: Array<{ key: keyof TriskellionStats; label: string }> = [
    { key: 'rage', label: 'Rage' },
    { key: 'fate', label: 'Fate' },
    { key: 'danger', label: 'Danger' },
];

const ArmoryEditSection: React.FC<ArmoryEditSectionProps> = ({ visibleTags, editingItem, setEditingItem }) => {
    const handleItemStatChange = (index: number, field: keyof LoadoutStatsWithRequirements | keyof TriskellionStats, value: any) => {
        const updatedItemStats = [...editingItem.stats];
        if (!updatedItemStats[index]) return;

        if (['attackReRolls', 'powerReRolls', 'autoHits'].includes(field)) {
            value = Math.max(0, value);
        } else if (['attackDice', 'maxHits'].includes(field)) {
            value = Math.max(value, 0);
        }

        if (field === 'maxHits' || field === 'autoHits') {
            value = Math.min(value, updatedItemStats[index].attackDice);
        } else if (field === 'attackDice') {
            updatedItemStats[index].maxHits = value;
            updatedItemStats[index].autoHits = Math.min(updatedItemStats[index].autoHits, value);
        }

        const maxHits = field === 'maxHits' ? value : updatedItemStats[index].maxHits;
        if (updatedItemStats[index].perHitPowerDiceOverride.length < maxHits) {
            updatedItemStats[index].perHitPowerDiceOverride = [...updatedItemStats[index].perHitPowerDiceOverride, ...Array(maxHits - updatedItemStats[index].perHitPowerDiceOverride.length).fill([])];
        } else if (updatedItemStats[index].perHitPowerDiceOverride.length > maxHits) {
            updatedItemStats[index].perHitPowerDiceOverride = updatedItemStats[index].perHitPowerDiceOverride.slice(0, maxHits);
        }

        if (field === 'overridePowerDice') {
            if (value) {
                updatedItemStats[index].perHitPowerDice = [];
            } else {
                updatedItemStats[index].perHitPowerDiceOverride = Array(maxHits).fill([]);
            }
        }

        updatedItemStats[index] = {
            ...updatedItemStats[index],
            [field]: value,
        };
        updatedItemStats[index] = Utils.sanitizeLoadoutStatsWithRequirements(updatedItemStats[index]);
        setEditingItem({ name: editingItem.name, tags: editingItem.tags, stats: updatedItemStats });
    };

    const handleDuplicateLoadout = (index: number) => {
        const updatedItemStats = [...editingItem.stats];
        if (!updatedItemStats[index]) return;

        const duplicatedLoadout = Utils.sanitizeLoadoutStatsWithRequirements(Utils.deepCopy(updatedItemStats[index]));
        updatedItemStats.push(duplicatedLoadout);

        setEditingItem({ name: editingItem.name, tags: editingItem.tags, stats: updatedItemStats });
    };

    const handleAddLoadout = () => {
        const updatedItemStats = [...editingItem.stats];
        const duplicatedLoadout = Utils.sanitizeLoadoutStatsWithRequirements(Utils.deepCopy(Constants.DEFAULT_LOADOUT_STATS));
        updatedItemStats.push(duplicatedLoadout);

        setEditingItem({ name: editingItem.name, tags: editingItem.tags, stats: updatedItemStats });
    };

    const handleRemoveLoadout = (index: number) => {
        const updatedItemStats = [...editingItem.stats];
        updatedItemStats.splice(index, 1);
        setEditingItem({ name: editingItem.name, tags: editingItem.tags, stats: updatedItemStats });
    };

    const handleIncrement = (
        index: number,
        field: keyof LoadoutStatsWithRequirements | keyof KratosTokens | keyof TriskellionStats,
        section: 'editingItem.stats' | 'autoTokens' | 'minTriskellion' | 'maxTriskellion',
        increment: number,
    ) => {
        if (section === 'editingItem.stats') {
            handleItemStatChange(index, field as keyof LoadoutStatsWithRequirements, ((editingItem.stats[index][field as keyof LoadoutStatsWithRequirements] as number) ?? 0) + increment);
        } else if (section === 'autoTokens') {
            handleAutoTokenChange(index, field as keyof KratosTokens, (editingItem.stats[index].autoTokens[field as keyof KratosTokens] ?? 0) + increment);
        } else if (section === 'minTriskellion' || section === 'maxTriskellion') {
            handleRequirementsChange(index, field as keyof TriskellionStats, (editingItem.stats[index].requirements[section][field as keyof TriskellionStats] ?? 0) + increment, section);
        }
    };

    const handleAutoTokenChange = (index: number, field: keyof KratosTokens, value: number) => {
        const updatedKratosTokens = editingItem.stats[index].autoTokens;
        updatedKratosTokens[field] = Math.max(value, 0);
        handleItemStatChange(index, 'autoTokens', updatedKratosTokens);
    };

    const handleRequirementsChange = (index: number, field: keyof TriskellionStats, value: number, section: 'minTriskellion' | 'maxTriskellion') => {
        const updatedRequirements = editingItem.stats[index].requirements;
        if (section === 'minTriskellion') {
            updatedRequirements.minTriskellion[field] = value;
        } else if (section === 'maxTriskellion') {
            updatedRequirements.maxTriskellion[field] = value;
        }
        handleItemStatChange(index, 'requirements', updatedRequirements);
    };

    const handleAddPowerDie = (index: number, field: keyof LoadoutStatsWithRequirements, color: PowerDieColor, diceIndex: number = 0) => {
        if (field === 'perHitPowerDiceOverride') {
            const updatedOverride = editingItem.stats[index][field] as PowerDieColor[][];
            const updatedDice = updatedOverride[diceIndex];
            updatedDice.push(color);
            handleItemStatChange(index, field, updatedOverride);
        } else {
            const updatedDice = editingItem.stats[index][field] as PowerDieColor[];
            updatedDice.push(color);
            handleItemStatChange(index, field, updatedDice);
        }
    };

    const handleRemovePowerDie = (index: number, field: keyof LoadoutStatsWithRequirements, subindex: number = 0) => {
        if (field === 'perHitPowerDiceOverride') {
            const updatedOverride = editingItem.stats[index][field] as PowerDieColor[][];
            const updatedDice = updatedOverride[subindex];
            updatedDice.pop();
            handleItemStatChange(index, field, updatedOverride);
        } else {
            const updatedDice = editingItem.stats[index][field] as PowerDieColor[];
            updatedDice.pop();
            handleItemStatChange(index, field, updatedDice);
        }
    };

    const renderDie = (die: PowerDieColor, key: string) => <span key={key} className={`die ${die.toLowerCase()}-die`} />;

    const renderDiceButtons = (onRemove: () => void, onAdd: (color: PowerDieColor) => void) => (
        <div className="increment-buttons">
            <div className="dice-buttons">
                <button type="button" className="dice-button decrement" onClick={onRemove}>
                    −
                </button>
                <button type="button" className="dice-button" onClick={() => onAdd(PowerDieColor.RED)}>
                    <span className="die red-die" />
                </button>
                <button type="button" className="dice-button" onClick={() => onAdd(PowerDieColor.BLACK)}>
                    <span className="die black-die" />
                </button>
                <button type="button" className="dice-button" onClick={() => onAdd(PowerDieColor.WHITE)}>
                    <span className="die white-die" />
                </button>
                <button type="button" className="dice-button" onClick={() => onAdd(PowerDieColor.MORTAL)}>
                    <span className="die mortal-die" />
                </button>
            </div>
        </div>
    );

    const renderDiceRow = (dice: PowerDieColor[], onRemove: () => void, onAdd: (color: PowerDieColor) => void) => (
        <div className="loadout-row">
            <div className="loadout-input-group">
                <div className="dice-display">{dice.map((die, dieIndex) => renderDie(die, `${die}-${dieIndex}`))}</div>
            </div>
            {renderDiceButtons(onRemove, onAdd)}
        </div>
    );

    const tokenRows = ARMORY_TOKEN_FIELDS.filter((field) => !field.spoiler || visibleTags.includes(field.spoiler)).reduce<Array<typeof ARMORY_TOKEN_FIELDS>>((rows, field, index, fields) => {
        if (index % 2 === 0) {
            rows.push(fields.slice(index, index + 2));
        }
        return rows;
    }, []);

    return (
        <div className="simulation-input-section armory-edit-section">
            <div className="add-loadout-container">
                <Button className="add-loadout-button" variant="secondary" size="lg" onClick={handleAddLoadout}>
                    Add Item Stat
                </Button>
            </div>

            <div className="loadout-section-container">
                {editingItem.stats.map((loadout, index) => (
                    <div className="loadout-section" key={index}>
                        <div className="loadout-title-row">
                            <input
                                type="text"
                                value={loadout.name}
                                onChange={(e) => handleItemStatChange(index, 'name', e.target.value)}
                                onFocus={(e) => e.currentTarget.select()}
                                placeholder={`Item Stat ${index + 1}`}
                                className="loadout-title-input"
                                aria-label={`Item stat ${index + 1} name`}
                            />
                            <Tooltip title={<pre className="tooltip-pre">{formatLoadoutInfo(loadout)}</pre>} arrow enterDelay={500} enterNextDelay={500}>
                                <Typography component="span" className="loadout-title-info">
                                    <InfoIcon className="info-icon" />
                                </Typography>
                            </Tooltip>
                        </div>

                        <p className="loadout-card-context">{editingItem.name || 'Unsaved item'}</p>

                        <div className="loadout-row">
                            <NumericField
                                label="Attack Dice"
                                value={loadout.attackDice}
                                onDecrement={() => handleIncrement(index, 'attackDice', 'editingItem.stats', -1)}
                                onIncrement={() => handleIncrement(index, 'attackDice', 'editingItem.stats', 1)}
                            />
                        </div>

                        <div className="loadout-row">
                            <NumericField
                                label="Max Hits"
                                value={loadout.maxHits}
                                onDecrement={() => handleIncrement(index, 'maxHits', 'editingItem.stats', -1)}
                                onIncrement={() => handleIncrement(index, 'maxHits', 'editingItem.stats', 1)}
                            />
                            <NumericField
                                label="Auto Hits"
                                value={loadout.autoHits}
                                onDecrement={() => handleIncrement(index, 'autoHits', 'editingItem.stats', -1)}
                                onIncrement={() => handleIncrement(index, 'autoHits', 'editingItem.stats', 1)}
                            />
                        </div>

                        <div className="loadout-row">
                            <NumericField
                                label="Precision Bonus"
                                value={loadout.precisionBonus}
                                onDecrement={() => handleIncrement(index, 'precisionBonus', 'editingItem.stats', -1)}
                                onIncrement={() => handleIncrement(index, 'precisionBonus', 'editingItem.stats', 1)}
                            />
                            <NumericField
                                label="Attack Re-Rolls"
                                value={loadout.attackReRolls}
                                onDecrement={() => handleIncrement(index, 'attackReRolls', 'editingItem.stats', -1)}
                                onIncrement={() => handleIncrement(index, 'attackReRolls', 'editingItem.stats', 1)}
                            />
                        </div>

                        <div className="loadout-row">
                            <NumericField
                                label="Power Bonus"
                                value={loadout.powerBonus}
                                onDecrement={() => handleIncrement(index, 'powerBonus', 'editingItem.stats', -1)}
                                onIncrement={() => handleIncrement(index, 'powerBonus', 'editingItem.stats', 1)}
                            />
                            <NumericField
                                label="Power Re-Rolls"
                                value={loadout.powerReRolls}
                                onDecrement={() => handleIncrement(index, 'powerReRolls', 'editingItem.stats', -1)}
                                onIncrement={() => handleIncrement(index, 'powerReRolls', 'editingItem.stats', 1)}
                            />
                        </div>

                        <div className="loadout-row">
                            <div className="loadout-input-group">
                                <label className="input-label">
                                    Override Per-Hit Power Dice?
                                    <div className="checkbox-container">
                                        <input type="checkbox" checked={loadout.overridePowerDice} onChange={(e) => handleItemStatChange(index, 'overridePowerDice', e.target.checked)} />
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div className="loadout-row">
                            <label>{loadout.overridePowerDice ? 'Power Dice' : 'Per-Hit Power Dice'}</label>
                        </div>
                        {loadout.overridePowerDice
                            ? renderDiceRow(
                                  loadout.perHitPowerDiceOverride[0] ?? [],
                                  () => handleRemovePowerDie(index, 'perHitPowerDiceOverride', 0),
                                  (color) => handleAddPowerDie(index, 'perHitPowerDiceOverride', color, 0),
                              )
                            : renderDiceRow(
                                  loadout.perHitPowerDice,
                                  () => handleRemovePowerDie(index, 'perHitPowerDice'),
                                  (color) => handleAddPowerDie(index, 'perHitPowerDice', color),
                              )}

                        {loadout.overridePowerDice &&
                            loadout.perHitPowerDiceOverride.map(
                                (powerDiceForHit, diceIndex) =>
                                    diceIndex > 0 && (
                                        <div key={`override-row-${diceIndex}`}>
                                            {renderDiceRow(
                                                powerDiceForHit,
                                                () => handleRemovePowerDie(index, 'perHitPowerDiceOverride', diceIndex),
                                                (color) => handleAddPowerDie(index, 'perHitPowerDiceOverride', color, diceIndex),
                                            )}
                                        </div>
                                    ),
                            )}

                        <div className="loadout-row">
                            <label>Additional Power Dice</label>
                        </div>
                        {renderDiceRow(
                            loadout.powerDice,
                            () => handleRemovePowerDie(index, 'powerDice'),
                            (color) => handleAddPowerDie(index, 'powerDice', color),
                        )}

                        {tokenRows.map((row, rowIndex) => (
                            <div key={`token-row-${rowIndex}`} className="loadout-row">
                                {row.map((field) => (
                                    <NumericField
                                        key={field.key}
                                        label={field.label}
                                        value={loadout.autoTokens[field.key] ?? 0}
                                        onDecrement={() => handleIncrement(index, field.key, 'autoTokens', -1)}
                                        onIncrement={() => handleIncrement(index, field.key, 'autoTokens', 1)}
                                        tokenKey={field.key}
                                    />
                                ))}
                            </div>
                        ))}

                        <div className="loadout-row">
                            {REQUIREMENT_FIELDS.map((field) => (
                                <NumericField
                                    key={`min-${field.key}`}
                                    label={`Min ${field.label}`}
                                    value={editingItem.stats[index].requirements.minTriskellion[field.key] || 0}
                                    onDecrement={() => handleIncrement(index, field.key, 'minTriskellion', -1)}
                                    onIncrement={() => handleIncrement(index, field.key, 'minTriskellion', 1)}
                                    accent={getArgonautStatPresentation(field.key)}
                                />
                            ))}
                        </div>

                        <div className="loadout-row">
                            {REQUIREMENT_FIELDS.map((field) => (
                                <NumericField
                                    key={`max-${field.key}`}
                                    label={`Max ${field.label}`}
                                    value={editingItem.stats[index].requirements.maxTriskellion[field.key] || 0}
                                    onDecrement={() => handleIncrement(index, field.key, 'maxTriskellion', -1)}
                                    onIncrement={() => handleIncrement(index, field.key, 'maxTriskellion', 1)}
                                    accent={getArgonautStatPresentation(field.key)}
                                />
                            ))}
                        </div>

                        <div className="spacer" />
                        <div className="input-section-button-container">
                            <Button variant="secondary" fullWidth onClick={() => handleDuplicateLoadout(index)}>
                                Duplicate Item Stat
                            </Button>
                            <Button variant="danger" fullWidth onClick={() => handleRemoveLoadout(index)}>
                                Remove Item Stat
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ArmoryEditSection;
